import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Mirrors SupportServiceCatalogItem::OPTION_BEARING_FIELD_TYPES — the field
// types whose answers come from a fixed option list. Keep the two in step.
const OPTION_BEARING_TYPES = ["select", "multiselect"]

// The field types Support::CatalogFormFieldComponent renders `rules`
// (min/max length, pattern) on. The editor has no input for rules, so a field
// moved to any other type drops them rather than carrying an invisible length
// check onto, say, a select's answer.
const RULE_BEARING_TYPES = ["text", "textarea", "email", "number", "date"]

// Admin visual editor for a service catalog item's form_schema JSONB.
//
// Field rows are server-rendered Ui::* components (see the _field_row partial),
// so the editor stays inside the design system. This controller only:
//   - clones the blank <template> row for "Add field" (swapping the __IDX__
//     placeholder for a unique id, like the action-builder pattern),
//   - toggles the select-only options editor,
//   - serializes the current rows (the DOM is the source of truth) to the
//     hidden form_schema input on every change and before submit,
//   - refreshes the server-rendered preview Turbo Frame (debounced).
//
// Reordering is delegated to the shared ui--draggable-list controller; this
// controller just re-serializes when it fires ui--draggable-list:reorder.
//
// data-controller="support--form-schema-editor"
export default class extends Controller {
  static targets = ["fieldList", "hiddenInput", "rowTemplate", "fieldRow"]
  static values = {
    previewUrl: String,
    conditionBlankMessage: String,
    conditionSingleMessage: String,
  }

  connect() {
    this._rowSeq = 0
    this._previewTimeout = null
    this._typeByRow = new Map()
    this._serialize()

    this._form = this.element.closest("form")
    if (this._form) {
      this._onSubmit = () => this._serialize()
      this._form.addEventListener("submit", this._onSubmit)
    }

    // Snapshot each row's Tom Select values before a drag starts — the reorder
    // re-parents the row, which resets the select (see #onReorder).
    this._onDragStart = () => this._snapshotSelectTypes()
    this.element.addEventListener("dragstart", this._onDragStart, true)
  }

  disconnect() {
    if (this._form && this._onSubmit) {
      this._form.removeEventListener("submit", this._onSubmit)
    }
    if (this._onDragStart) {
      this.element.removeEventListener("dragstart", this._onDragStart, true)
    }
    if (this._previewTimeout) clearTimeout(this._previewTimeout)
  }

  addField() {
    if (!this.hasRowTemplateTarget) return

    const uid = `n${(this._rowSeq += 1)}`
    const fragment = this.rowTemplateTarget.content.cloneNode(true)

    fragment
      .querySelectorAll(
        "[id], [name], [for], [aria-labelledby], [aria-controls], [aria-describedby]",
      )
      .forEach((el) => {
        for (const attr of [
          "id",
          "name",
          "for",
          "aria-labelledby",
          "aria-controls",
          "aria-describedby",
        ]) {
          const val = el.getAttribute(attr)
          if (val && val.includes("__IDX__")) {
            el.setAttribute(attr, val.replaceAll("__IDX__", uid))
          }
        }
      })

    this.fieldListTarget.appendChild(fragment)
    this._serialize()
  }

  removeField(event) {
    const row = event.target.closest(
      "[data-support--form-schema-editor-target='fieldRow']",
    )
    if (row) row.remove()
    this._serialize()
  }

  // Show the options editor only for select fields. Keeps the row markup stable
  // (no re-render) — the textarea is always present, just hidden otherwise.
  typeChanged(event) {
    const row = event.target.closest(
      "[data-support--form-schema-editor-target='fieldRow']",
    )
    if (row) {
      const optionsField = row.querySelector("[data-fse-options]")
      if (optionsField)
        optionsField.hidden = !OPTION_BEARING_TYPES.includes(event.target.value)
      this._syncOptionSourceFor(event.target)
    }
    this._serialize()
  }

  configChanged(event) {
    // CONEK-5794: a field takes its options EITHER from a shared list or from
    // the inline textarea — the model rejects both being set. Showing the two
    // together let an admin fill in a combination that only failed on save, so
    // hide the inline editor as soon as a shared list is chosen.
    if (event) this._syncOptionSourceFor(event.target)
    this._serialize()
  }

  // Hides the inline options textarea while a shared option list is selected on
  // the same row. Idempotent, so it is also safe to call on connect/type change.
  _syncOptionSourceFor(el) {
    const row = el.closest(
      "[data-support--form-schema-editor-target='fieldRow']",
    )
    if (!row) return

    const optionSetSelect = row.querySelector("[data-field='option_set_key']")
    const optionsTextarea = row.querySelector("[data-field='options']")
    if (!optionSetSelect || !optionsTextarea) return

    const usesSharedList = optionSetSelect.value.trim() !== ""
    // Toggle the textarea's own wrapper when it has one, so the label goes with
    // it rather than leaving a stray floating label behind.
    const target = optionsTextarea.closest("label") || optionsTextarea
    target.hidden = usesSharedList
  }

  // The drag re-parents the rows, so Stimulus destroys + recreates each row's
  // Tom Select (ui--select). Tom Select's destroy() reverts the <select> to its
  // init-time value, silently dropping the user's choice (the type, or since
  // CONEK-9334 the condition operator). We snapshot every select on dragstart and restore them once Stimulus has reconnected the moved
  // selects — that happens on the MutationObserver microtask, so we defer past
  // it with requestAnimationFrame before restoring and re-serializing.
  onReorder() {
    requestAnimationFrame(() => {
      this._restoreSelectTypes()
      this._serialize()
    })
  }

  _snapshotSelectTypes() {
    this._typeByRow = new Map()
    this.fieldRowTargets.forEach((row) => {
      const values = new Map()
      row
        .querySelectorAll("select[data-field]")
        .forEach((select) => values.set(select.dataset.field, select.value))
      this._typeByRow.set(row, values)
    })
  }

  _restoreSelectTypes() {
    this.fieldRowTargets.forEach((row) => {
      const values = this._typeByRow.get(row)
      if (!values) return

      row.querySelectorAll("select[data-field]").forEach((select) => {
        const wanted = values.get(select.dataset.field)
        if (wanted == null || select.value === wanted) return

        if (select.tomselect) select.tomselect.setValue(wanted, true)
        else select.value = wanted
      })

      // Keep the select-only options editor in sync with the restored type.
      const optionsField = row.querySelector("[data-fse-options]")
      const type = values.get("type")
      if (optionsField && type != null)
        optionsField.hidden = !OPTION_BEARING_TYPES.includes(type)
    })
  }

  beforeSubmit(event) {
    this._serialize()
    if (!this._validateConditions()) event?.preventDefault()
  }

  // CONEK-9334: the save path only shows a generic error, so catch the two
  // condition mistakes an admin can make here, next to the row that has them.
  _validateConditions() {
    let valid = true

    this.fieldRowTargets.forEach((row) => {
      const message = this._conditionError(row)
      const errorEl = row.querySelector("[data-fse-condition-error]")
      const valuesEl = row.querySelector("[data-field='visible_if_values']")
      if (errorEl) {
        errorEl.textContent = message || ""
        errorEl.hidden = !message
      }
      valuesEl?.classList.toggle("border-red-500", Boolean(message))
      if (message) valid = false
    })

    return valid
  }

  _conditionError(row) {
    const condition = this._readRow(row).visible_if
    if (!condition) return null

    if ("equals" in condition) {
      if (!condition.equals) return this.conditionBlankMessageValue
      if (condition.equals.includes("\n"))
        return this.conditionSingleMessageValue
      return null
    }

    const values = condition.equals_any || condition.equals_none
    return values.length === 0 ? this.conditionBlankMessageValue : null
  }

  // The DOM is the source of truth: read every row, keep the ones with a key and
  // type, and write the schema JSON to the hidden input the form submits.
  _serialize() {
    const fields = this.fieldRowTargets
      .map((row) => this._readRow(row))
      .filter((field) => field.key && field.type)

    const schema = fields.length > 0 ? { fields } : {}

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = JSON.stringify(schema)
    }

    this._schedulePreview(schema)
  }

  _readRow(row) {
    // CONEK-9334: start from the stored field so keys without an input here
    // (rules, for one) survive a save; the inputs below overwrite the rest.
    const original = this._originalField(row)
    delete original.visible_if
    const field = {
      ...original,
      key: "",
      type: "text",
      label: "",
      required: false,
      options: [],
    }
    let visibleIfField = ""
    let visibleIfOperator = "equals"
    let visibleIfValues = []

    row.querySelectorAll("[data-field]").forEach((el) => {
      const prop = el.dataset.field
      if (prop === "required") {
        field.required = el.checked
      } else if (prop === "options") {
        field.options = this._parseOptions(el.value)
      } else if (prop === "visible_if_field") {
        visibleIfField = el.value.trim()
      } else if (prop === "visible_if_operator") {
        visibleIfOperator = el.value || "equals"
      } else if (prop === "visible_if_values") {
        visibleIfValues = el.value
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
      } else {
        field[prop] = el.value
      }
    })

    // Only select/multiselect carry options — see
    // SupportServiceCatalogItem::OPTION_BEARING_FIELD_TYPES.
    if (!OPTION_BEARING_TYPES.includes(field.type)) {
      field.options = []
      delete field.option_set_key
    }

    if (!RULE_BEARING_TYPES.includes(field.type)) delete field.rules

    // A referenced shared list wins over inline options; the model rejects both
    // being set at once.
    if (field.option_set_key) {
      field.options = []
    } else {
      delete field.option_set_key
    }

    // Omit empty optional keys rather than emitting "" — a blank help string or
    // a half-filled condition should not end up in the stored schema.
    if (!field.help) delete field.help
    // `equals` takes one value. Several lines are kept as typed so the admin
    // gets an error instead of having all but one silently dropped.
    if (visibleIfField) {
      field.visible_if = {
        field: visibleIfField,
        [visibleIfOperator]:
          visibleIfOperator === "equals"
            ? visibleIfValues.join("\n")
            : visibleIfValues,
      }
    }

    return field
  }

  _originalField(row) {
    try {
      const parsed = JSON.parse(row.dataset.fseOriginal || "{}")
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {}
    } catch {
      return {}
    }
  }

  // Accepts "Label | value" per line so a label can be reworded without
  // invalidating answers already stored against the value, and a bare line for
  // the common case where they are the same.
  _parseOptions(raw) {
    return raw
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separator = line.indexOf("|")
        if (separator === -1) return line

        const label = line.slice(0, separator).trim()
        const value = line.slice(separator + 1).trim()
        if (!value) return label
        return label && label !== value ? { label, value } : value
      })
  }

  _schedulePreview(schema) {
    if (!this.hasPreviewUrlValue) return
    if (this._previewTimeout) clearTimeout(this._previewTimeout)
    this._previewTimeout = setTimeout(() => this._refreshPreview(schema), 300)
  }

  async _refreshPreview(schema) {
    try {
      await post(this.previewUrlValue, {
        body: JSON.stringify({ form_schema: schema }),
        contentType: "application/json",
        responseKind: "turbo-stream",
      })
    } catch (error) {
      console.error(
        "[support--form-schema-editor] preview refresh failed:",
        error,
      )
    }
  }
}
